import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

/**
 * The irregular-edged highlighter stroke — the editorial "one yellow moment".
 * A hand-pulled SVG path (wavy top and bottom, ragged entry and exit) laid
 * under the words, so the mark reads drawn rather than machine-made the way the
 * straight `.highlighter` CSS gradient does. Fill comes from the yellow token,
 * never a re-typed hex.
 *
 * Two grounds:
 *  - default (paper / cream): the stroke multiplies at 0.85 so a whisper of
 *    paper still comes through and the ink text below it stays legible.
 *  - `onStrong` (navy masthead, `bg-surface-strong`): multiply would drag the
 *    yellow muddy against the dark ground, so we strike it solid and flip the
 *    text to ink — the masthead's loudest, most deliberate moment.
 */
export function HighlighterStroke({
  children,
  onStrong = false,
  className,
}: {
  children: ReactNode
  onStrong?: boolean
  className?: string
}) {
  return (
    // textIndent 0: an indented paragraph must not re-indent inside this
    // inline-block, or the stroke overhangs the gap.
    <span
      className={cn(
        "relative inline-block whitespace-nowrap",
        onStrong && "text-batyr-ink font-semibold",
        className,
      )}
      style={{ textIndent: 0 }}
    >
      <svg
        aria-hidden="true"
        className="absolute inset-y-0 -left-1 h-full w-[calc(100%+0.5rem)]"
        viewBox="0 0 100 32"
        preserveAspectRatio="none"
        style={onStrong ? undefined : { mixBlendMode: "multiply" }}
      >
        <path
          d="M3 9 C 18 5.5, 42 8, 61 6.5 S 94 8.5, 98 6 L 97 25 C 78 29, 47 25.5, 22 28 S 5 27, 2 24 Z"
          fill="var(--color-batyr-yellow)"
          // On paper the scoped soft gold reads as a drawn band at 0.85 while a
          // whisper of paper still comes through the multiply. On the navy
          // ground there is no multiply, so strike it fully opaque.
          opacity={onStrong ? 1 : 0.85}
        />
      </svg>
      <span className="relative">{children}</span>
    </span>
  )
}
